/**
 * useAccountUsage
 *
 * Fetches engagement rollups for every linked customer org on the Accounts
 * board in a single call, keyed by customerOrgId. Powers the at-a-glance stats
 * rendered on linked account cards.
 *
 * Superadmin-only (the endpoint reads cross-org data) — gated via `enabled` so
 * non-superadmins simply render the board without stats and trigger no request.
 *
 * @module features/accounts/hooks/useAccountUsage
 */
import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';
import { useAuth } from '../../../contexts/AuthContext';

export type AccountUsageStats = {
  dau: number;
  wau: number;
  mau: number;
  runsPerDay: number;
  /** Integer percent of passing runs over 30d; null when there were no runs. */
  passRate: number | null;
  usedCli: boolean;
  usedExtension: boolean;
  usedUi: boolean;
};

/** Map keyed by linked customer org id. */
export type AccountUsageMap = Record<string, AccountUsageStats>;

export function useAccountUsage() {
  const { user } = useAuth();
  const isSuperadmin = user?.isSuperadmin === true;

  return useQuery({
    queryKey: ['account-board', 'usage'],
    enabled: isSuperadmin,
    queryFn: async (): Promise<AccountUsageMap> => {
      const res = await fetch(`${API_URL}/superadmin/account-board/accounts/usage`, {
        credentials: 'include',
      });
      if (!res.ok) {
        throw await toApiError(res, 'Failed to fetch account usage');
      }
      const data = (await res.json()) as { ok: true; usage: AccountUsageMap };
      return data.usage;
    },
  });
}
